import { supabase } from "../supabase";
import { getActiveOrganizationId, subscribeActiveOrg } from "../org";
import type {
  TaxPolicy,
  TaxPolicyRegion,
  TaxPolicyStatus,
  TaxPolicyStore,
} from "./types";

interface TaxPolicyRow {
  id: string;
  category: string;
  category_id: string | null;
  region: TaxPolicyRegion;
  deductibility: string;
  vat_rule: string | null;
  requirements: string[] | null;
  note: string | null;
  reference: string | null;
  reference_url: string | null;
  status: TaxPolicyStatus;
}

function rowToPolicy(row: TaxPolicyRow): TaxPolicy {
  return {
    id: row.id,
    category: row.category,
    categoryId: row.category_id ?? null,
    region: row.region,
    deductibility: row.deductibility,
    vatRule: row.vat_rule ?? "",
    requirements: row.requirements ?? [],
    note: row.note,
    reference: row.reference ?? "",
    referenceUrl: row.reference_url ?? "",
    status: row.status,
  };
}

export function createSupabaseTaxPolicyStore(): TaxPolicyStore {
  const listeners = new Set<() => void>();
  let snapshot: TaxPolicy[] = [];
  let initialized = false;
  let loadPromise: Promise<void> | null = null;

  function emit(): void {
    for (const listener of listeners) listener();
  }

  function commit(next: TaxPolicy[]): void {
    snapshot = next;
    emit();
  }

  async function loadFromServer(): Promise<void> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      commit([]);
      return;
    }
    const orgId = getActiveOrganizationId();
    if (!orgId) {
      commit([]);
      return;
    }
    const { data, error } = await supabase
      .from("tax_policies")
      .select(
        "id, category, category_id, region, deductibility, vat_rule, requirements, note, reference, reference_url, status",
      )
      .eq("organization_id", orgId)
      .order("category", { ascending: true });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`[taxPolicies] list failed: ${error.message}`);
      commit([]);
      return;
    }
    const rows = (data as TaxPolicyRow[] | null) ?? [];
    commit(rows.map(rowToPolicy));
  }

  function ensureLoaded(): Promise<void> {
    if (initialized) return Promise.resolve();
    if (!loadPromise) {
      loadPromise = loadFromServer().finally(() => {
        initialized = true;
        loadPromise = null;
      });
    }
    return loadPromise;
  }

  let authSubAttached = false;
  function attachAuthListener(): void {
    if (authSubAttached) return;
    authSubAttached = true;
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        initialized = false;
        commit([]);
        return;
      }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        initialized = false;
        void ensureLoaded();
      }
    });
    subscribeActiveOrg(() => {
      initialized = false;
      void ensureLoaded();
    });
  }

  return {
    list(): TaxPolicy[] {
      if (!initialized && !loadPromise) void ensureLoaded();
      return snapshot;
    },
    subscribe(listener: () => void): () => void {
      attachAuthListener();
      if (!initialized && !loadPromise) void ensureLoaded();
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async refresh(): Promise<void> {
      initialized = false;
      await ensureLoaded();
    },
  };
}
